/**
 * `/forgot` and `/reset` — the two pages of the password-reset flow.
 *
 * The rule that shapes both: **`POST /auth/forgot` always answers 200**, for an
 * unknown address as for a known one, so this page can never say "we have sent you
 * a mail". It says "if an account exists" and means it. `/reset` is reached from
 * the link in that mail (`{PUBLIC_URL}/reset?token=…`), spends the token once, and
 * hands the pilot to `/login` — the reset drops every session on purpose, so there
 * is nothing to sign in to but the form.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as api from '../api';
import { AuthLayout } from '../app/AuthLayout';
import { useT } from '../i18n';
import { emailLooksValid, MIN_PASSWORD, passwordProblem, tokenFromSearch } from '../password';

export function Forgot() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<api.FormError>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!emailLooksValid(email)) {
      setError({ key: 'pw.err.email' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      // the endpoint answers 200 to anything it understands; this is the network
      setError({ err });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('pw.forgot.title')} lead={t('pw.forgot.lead')}>
        {sent ? (
          <div className="card stack">
            <p className="ok">{t('pw.forgot.sent')}</p>
            <p className="note">{t('pw.forgot.sent.hint')}</p>
            <div className="row">
              <Link className="linkbtn" to="/login">
                {t('pw.tologin')}
              </Link>
            </div>
          </div>
        ) : (
          <form className="card stack" onSubmit={e => void submit(e)}>
            <label>
              {t('auth.email')}
              <input
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </label>
            {error ? <p className="err">{api.formMessage(t, error)}</p> : null}
            <div className="row">
              <button type="submit" className="primary" disabled={busy}>
                {busy ? t('auth.busy') : t('pw.forgot.send')}
              </button>
              <Link className="note" to="/login">
                {t('pw.tologin')}
              </Link>
            </div>
          </form>
        )}
    </AuthLayout>
  );
}

export function Reset() {
  const t = useT();
  const navigate = useNavigate();
  const token = tokenFromSearch(useLocation().search);
  const [pw, setPw] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<api.FormError>(null);
  const [busy, setBusy] = useState(false);
  /* 400 invalid_token is not a form error: the form is fine, the link is not */
  const [dead, setDead] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = passwordProblem(pw, repeat);
    if (problem) {
      setError({ key: 'pw.err.' + problem, vars: { n: MIN_PASSWORD } });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, pw);
      // every session of that account is gone now, including any this browser had
      navigate('/login', { replace: true, state: { notice: 'pw.reset.done' } });
    } catch (err) {
      if (err instanceof api.ApiError && err.code === 'invalid_token') setDead(true);
      else setError({ err });
    } finally {
      setBusy(false);
    }
  };

  const broken = (text: string) => (
    <AuthLayout title={t('pw.reset.title')}>
        <div className="card stack">
          <p className="err">{text}</p>
          <div className="row">
            <Link className="linkbtn" to="/forgot">
              {t('pw.reset.again')}
            </Link>
            <Link className="note" to="/login">
              {t('pw.tologin')}
            </Link>
          </div>
        </div>
    </AuthLayout>
  );

  if (!token) return broken(t('pw.reset.notoken'));
  if (dead) return broken(t('err.invalid_token'));

  return (
    <AuthLayout title={t('pw.reset.title')} lead={t('pw.reset.lead')}>
        <form className="card stack" onSubmit={e => void submit(e)}>
          <label>
            {t('pw.new')} <span className="note">{t('pw.rule', { n: MIN_PASSWORD })}</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              autoFocus
              value={pw}
              onChange={e => setPw(e.target.value)}
            />
          </label>
          <label>
            {t('pw.repeat')}
            <input
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={e => setRepeat(e.target.value)}
            />
          </label>
          {error ? <p className="err">{api.formMessage(t, error)}</p> : null}
          <div className="row">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? t('auth.busy') : t('pw.reset.save')}
            </button>
            <Link className="note" to="/forgot">
              {t('pw.reset.again')}
            </Link>
          </div>
        </form>
    </AuthLayout>
  );
}
